// @flow

import * as React from 'react';
import {useState, useContext, useEffect} from 'react';
import {useStoreState} from 'react-flow-renderer';
import {contextStubFunction} from 'docs/util';
import {every, cloneDeep} from 'lodash';
import {
  buildOverviewDiagram,
  buildSequenceDiagram,
  buildStateDiagram,
  buildEntityDiagram,
  positionElements,
  positionSequenceElements,
  hasDimensions,
} from 'docs/components/core/Diagram/util';
import {diagramMarkdoc} from './diagramMarkdoc';
import {mockNode} from './mockNode';
import {mockEdge} from './mockEdge';
import type {MockNodeAttributes} from './mockNode';
import type {
  DiagramType,
  Element,
  Edge,
} from '../../components/core/Diagram/types';

const BUILD = {
  overview: buildOverviewDiagram,
  state: buildStateDiagram,
  sequence: buildSequenceDiagram,
  entity: buildEntityDiagram,
};

export type DiagramGeneratorContextProps = {
  type: DiagramType,
  elements: Array<Element>,
  markdoc: string,
  showHandles: boolean,
  showNodeModal: boolean,
  showEdgeModal: boolean,
  setShowHandles: (boolean) => void,
  setShowNodeModal: (boolean) => void,
  setShowEdgeModal: (boolean) => void,
  setType: (DiagramType) => void,
  setDescription: (string) => void,
  addNode: (MockNodeAttributes, string | Array<any>) => void,
  addEdge: (Edge) => void,
  addEdgeContent: (string, string) => void,
  reset: () => void,
  resetNodePositions: () => void,
  onNodeDragStop: (any) => void,
};

const DiagramGeneratorContext = React.createContext<DiagramGeneratorContextProps>(
  {
    type: 'overview',
    elements: [],
    markdoc: '',
    showHandles: true,
    showNodeModal: false,
    showEdgeModal: false,
    setShowHandles: contextStubFunction('DiagramGenerator'),
    setShowNodeModal: contextStubFunction('DiagramGenerator'),
    setShowEdgeModal: contextStubFunction('DiagramGenerator'),
    setType: contextStubFunction('DiagramGenerator'),
    setDescription: contextStubFunction('DiagramGenerator'),
    addNode: contextStubFunction('DiagramGenerator'),
    addEdgeContent: contextStubFunction('DiagramGenerator'),
    addEdge: contextStubFunction('DiagramGenerator'),
    reset: contextStubFunction('DiagramGenerator'),
    resetNodePositions: contextStubFunction('DiagramGenerator'),
    onNodeDragStop: contextStubFunction('DiagramGenerator'),
  },
);

type DiagramGeneratorProviderProps = {|
  initialType: DiagramType,
  children: React.Node,
|};

export const DiagramGeneratorProvider = ({
  initialType,
  children,
}: DiagramGeneratorProviderProps) => {
  // Internal state provided by react-flow-renderer to let us know the dimensions of the rendered nodes
  const diagramNodes = useStoreState((store) => store.nodes);
  const diagramEdges = useStoreState((store) => store.edges);

  // State to keep track of the nodes and edges the user is creating
  const [type, setType] = useState(initialType);
  const [description, setDescription] = useState('');
  const [elements, setElements] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [edge, setEdge] = useState({from: '', to: ''});
  const [markdoc, setMarkdoc] = useState(
    diagramMarkdoc(type, description, [], []),
  );

  // UI helpers
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showEdgeModal, setShowEdgeModal] = useState(false);
  const [buildGraph, setBuildGraph] = useState(true);
  const [showHandles, setShowHandles] = useState(true);

  useEffect(() => {
    // only build the graph if the nodes have been rendered on the page
    // so we know their height and width before calculating their position
    if (
      buildGraph &&
      diagramNodes?.length + diagramEdges?.length === elements.length &&
      every(diagramNodes, hasDimensions)
    ) {
      const positionedElements =
        type === 'sequence'
          ? positionSequenceElements(diagramNodes, elements)
          : positionElements(diagramNodes, elements);

      setBuildGraph(false);
      setElements(positionedElements);
    }
  }, [diagramNodes, diagramEdges, elements, buildGraph, type]);

  useEffect(() => {
    const build = BUILD[type];
    if (build) {
      // Construct the set of elements to render into the diagram
      const els = build({type}, [...nodes, ...edges]);
      setElements(els);
      setBuildGraph(true);
      // Update the sample Markdoc shown
      setMarkdoc(diagramMarkdoc(type, description, nodes, edges));
    }
  }, [nodes, edges, description, type]);

  // Adds a new node to the diagram
  // {id, text, state, icon, handles}
  const addNode = (
    attributes: MockNodeAttributes,
    content: string | Array<any>,
  ) => {
    const n = mockNode(attributes, content);
    setNodes([...nodes, n]);
    setShowNodeModal(false);
  };

  // Takes any content the user entered and constructs
  // a new edge with the content
  const addEdgeContent = (label: string, event?: string) => {
    const e = mockEdge(edge, label, event);
    setEdges([...edges, e]);
    setShowEdgeModal(false);
  };

  // Adds a new edge to the diagram and prompts the user
  // to add a label to the edge if needed
  const addEdge = ({source, target, sourceHandle}: Edge) => {
    if (source === target) {
      // Don't draw edges that connect to the same node
      return;
    }
    setEdge({
      from: sourceHandle === 'generator' ? source : sourceHandle || '',
      to: target,
    });
    setShowEdgeModal(true);
  };

  // Resets the diagram
  const reset = () => {
    setNodes([]);
    setEdges([]);
  };

  // Removes any manual positioning from the nodes and autopositions them
  const resetNodePositions = () => {
    const newNodes = nodes.map((n) => ({
      ...n,
      attributes: {...n.attributes, x: 0, y: 0},
    }));
    setNodes(newNodes);
  };

  // Sets the x/y positions on the dragged node
  const onNodeDragStop = (event: any, node: any) => {
    const draggedNode = nodes.find((n) => n.attributes.id === node.id);
    const index = nodes.indexOf(draggedNode);
    if (draggedNode) {
      const positionedNode = cloneDeep(draggedNode);
      positionedNode.attributes.x = Math.round(node.position.x);
      positionedNode.attributes.y = Math.round(node.position.y);

      const updatedNodes = [
        ...nodes.slice(0, index),
        positionedNode,
        ...nodes.slice(index + 1, nodes.length),
      ];
      setNodes(updatedNodes);
    }
  };

  const value = {
    type,
    elements,
    markdoc,
    showHandles,
    showNodeModal,
    showEdgeModal,
    setShowNodeModal,
    setShowEdgeModal,
    setShowHandles,
    setType,
    setDescription,
    addNode,
    addEdge,
    addEdgeContent,
    reset,
    resetNodePositions,
    onNodeDragStop,
  };

  return (
    <DiagramGeneratorContext.Provider value={value}>
      {children}
    </DiagramGeneratorContext.Provider>
  );
};

export const useDiagramGenerator = () => {
  return useContext(DiagramGeneratorContext);
};
